import React, { useState, useMemo } from 'react';
import { X, MessageCircle, ChevronRight, HelpCircle } from 'lucide-react';
import { PrimaryButton } from './ui';
import { CATEGORIES, getQuestionsForCategory } from '@/data';
import type { CoupleData, CategoryQAResponse } from '@/types';

interface CategoryQAModalProps {
  categoryId: string;
  coupleData: CoupleData;
  onSave: (response: CategoryQAResponse) => Promise<void>;
  onClose: () => void;
}

export const CategoryQAModal: React.FC<CategoryQAModalProps> = ({
  categoryId,
  coupleData,
  onSave,
  onClose
}) => {
  const category = CATEGORIES.find(c => c.id === categoryId);
  const questions = useMemo(() => getQuestionsForCategory(categoryId), [categoryId]);

  // Get already answered question IDs for this category
  const existingAnswers = coupleData.categoryQA?.[categoryId]?.answers || {};

  // Filter to unanswered questions
  const unansweredQuestions = questions.filter(q => !existingAnswers[q.id]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>(existingAnswers);
  const [saving, setSaving] = useState(false);

  const currentQuestion = unansweredQuestions[currentIndex];
  const isLastQuestion = currentIndex === unansweredQuestions.length - 1;
  const progress = ((currentIndex + 1) / unansweredQuestions.length) * 100;

  const handleAnswer = (value: string) => {
    if (!currentQuestion) return;
    setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleNext = async () => {
    if (isLastQuestion) {
      setSaving(true);
      try {
        await onSave({
          categoryId,
          answeredAt: new Date().toISOString(),
          answers
        });
        onClose();
      } catch (err) {
        console.error('Failed to save Q&A:', err);
      } finally {
        setSaving(false);
      }
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handleSkip = () => {
    if (isLastQuestion) {
      onClose();
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  };

  if (unansweredQuestions.length === 0 || !currentQuestion) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-2xl border border-zinc-700 w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{category?.emoji}</span>
            <div>
              <h3 className="text-white font-semibold">Help us curate for you</h3>
              <p className="text-zinc-400 text-sm">{category?.name} preferences</p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-zinc-800">
          <div
            className="h-full bg-gradient-to-r from-pink-500 to-purple-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <MessageCircle className="w-5 h-5 text-pink-400 flex-shrink-0 mt-1" />
            <div>
              <p className="text-white text-lg font-medium">{currentQuestion.question}</p>
              {currentQuestion.forWho !== 'both' && (
                <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-zinc-400 mt-1 inline-block">
                  For {currentQuestion.forWho === 'her' ? coupleData.herName : coupleData.hisName}
                </span>
              )}
            </div>
          </div>

          {/* Context/Educational info */}
          {currentQuestion.context && (
            <div className="flex items-start gap-2 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4">
              <HelpCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-blue-300 text-sm">{currentQuestion.context}</p>
            </div>
          )}

          {/* Answer input based on type */}
          {currentQuestion.type === 'text' && (
            <textarea
              value={answers[currentQuestion.id] || ''}
              onChange={e => handleAnswer(e.target.value)}
              placeholder={currentQuestion.placeholder || 'Share your thoughts...'}
              rows={4}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white resize-none focus:border-pink-500 focus:outline-none"
            />
          )}

          {currentQuestion.type === 'choice' && currentQuestion.choices && (
            <div className="space-y-2">
              {currentQuestion.choices.map(choice => (
                <button
                  key={choice}
                  onClick={() => handleAnswer(choice)}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${
                    answers[currentQuestion.id] === choice
                      ? 'bg-pink-500/20 border-pink-500 text-pink-300'
                      : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                  }`}
                >
                  {choice}
                </button>
              ))}
            </div>
          )}

          {currentQuestion.type === 'scale' && (
            <div className="flex justify-between gap-2">
              {[1, 2, 3, 4, 5].map(num => (
                <button
                  key={num}
                  onClick={() => handleAnswer(String(num))}
                  className={`flex-1 py-3 rounded-xl border text-lg font-bold transition-all ${
                    answers[currentQuestion.id] === String(num)
                      ? 'bg-pink-500/20 border-pink-500 text-pink-300'
                      : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
          )}

          {/* Anonymous note */}
          <p className="text-zinc-500 text-xs mt-4 text-center">
            Your answers help us suggest videos that match your preferences. Always anonymous.
          </p>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-700 flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-zinc-400 hover:text-white text-sm"
          >
            Skip this question
          </button>
          <div className="flex items-center gap-2">
            <span className="text-zinc-500 text-sm">
              {currentIndex + 1} of {unansweredQuestions.length}
            </span>
            <PrimaryButton
              onClick={handleNext}
              disabled={!answers[currentQuestion.id] || saving}
            >
              {isLastQuestion ? 'Save & Close' : 'Next'} <ChevronRight className="w-4 h-4" />
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

// Prompt component to show when entering a category
export const CategoryQAPrompt: React.FC<{
  categoryId: string;
  coupleData: CoupleData;
  onStartQA: () => void;
  onDismiss: () => void;
}> = ({ categoryId, coupleData, onStartQA, onDismiss }) => {
  const category = CATEGORIES.find(c => c.id === categoryId);
  const questions = getQuestionsForCategory(categoryId);
  const existingAnswers = coupleData.categoryQA?.[categoryId]?.answers || {};
  const unansweredCount = questions.filter(q => !existingAnswers[q.id]).length;

  if (unansweredCount === 0) return null;

  return (
    <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/30 rounded-xl p-4 mb-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">{category?.emoji}</span>
          <div>
            <h4 className="text-white font-semibold">Help us curate {category?.name} for you</h4>
            <p className="text-zinc-400 text-sm mt-1">
              Answer {unansweredCount} quick question{unansweredCount !== 1 ? 's' : ''} so we can suggest the best videos based on your preferences.
            </p>
          </div>
        </div>
        <button onClick={onDismiss} className="text-zinc-500 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={onStartQA}
          className="flex items-center gap-2 px-4 py-2 bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 rounded-lg text-sm font-medium transition-colors"
        >
          <MessageCircle className="w-4 h-4" /> Answer Questions
        </button>
      </div>
    </div>
  );
};
